import React, { useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";

interface CancellationReasonModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (
    reason: string,
    extra?: { cancellationCategory?: "my_cinemas_issue" | "customer_initiated" },
  ) => void;
  title?: string;
  message?: string;
  bookingCount?: number;
  isLoading?: boolean;
  /**
   * If true, surface the Cancellation Category dropdown.
   * Used for the admin cancel/refund flows where the category determines
   * the refund policy (MY Cinemas Issue = full refund incl. conv. fee,
   * Customer Initiated = 20% retention on ticket).
   */
  showCategory?: boolean;
  defaultCategory?: "my_cinemas_issue" | "customer_initiated";
}

const CancellationReasonModal: React.FC<CancellationReasonModalProps> = ({
  show,
  onHide,
  onConfirm,
  title = "Cancellation Reason",
  message,
  bookingCount,
  isLoading = false,
  showCategory = false,
  defaultCategory = "my_cinemas_issue",
}) => {
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState<
    "my_cinemas_issue" | "customer_initiated"
  >(defaultCategory);
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!reason.trim()) {
      setError("Cancellation reason is required");
      return;
    }

    onConfirm(reason.trim(), { cancellationCategory: category });
    setReason("");
    setCategory(defaultCategory);
    setError("");
  };

  const handleClose = () => {
    setReason("");
    setCategory(defaultCategory);
    setError("");
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {message && <p className="mb-3">{message}</p>}
          {bookingCount && bookingCount > 1 && (
            <p className="text-muted mb-3">
              This reason will apply to all {bookingCount} selected booking(s).
            </p>
          )}

          {showCategory && (
            <Form.Group className="mb-3">
              <Form.Label>
                Cancellation Category <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={category}
                onChange={(e) =>
                  setCategory(
                    e.target.value as
                      | "my_cinemas_issue"
                      | "customer_initiated",
                  )
                }
              >
                <option value="my_cinemas_issue">
                  MY Cinemas Issue (full refund incl. convenience fee)
                </option>
                <option value="customer_initiated">
                  Customer Initiated (20% retention on ticket amount)
                </option>
              </Form.Select>
              <Form.Text className="text-muted">
                <strong>MY Cinemas Issue</strong> — show cancelled, technical
                issue, theatre-side problem. Customer gets full refund
                including convenience fee.
                <br />
                <strong>Customer Initiated</strong> — admin cancelling on
                behalf of customer for customer-side reasons (fraud, no-show,
                etc.). 20% cancellation charge retained on ticket amount.
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group>
            <Form.Label>
              Cancellation Reason <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError("");
              }}
              placeholder="Enter the reason for cancellation..."
              isInvalid={!!error}
              autoFocus={!showCategory}
            />
            {error && (
              <Form.Control.Feedback type="invalid">
                {error}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="danger" type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              "Confirm"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CancellationReasonModal;
